import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import salesApiClient from '@/api/salesClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogOverlay } from '@/components/ui/dialog';
import { Plus, CheckCircle2, Circle, Clock, AlertCircle, LayoutGrid, List, Search } from 'lucide-react';
import { format } from 'date-fns';

export default function Tasks() {
  const [searchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    assigned_to: user.id,
    priority: 'medium',
  });

  useEffect(() => {
    fetchTasks();
    fetchUsers();
    // Update filter status from URL params
    const statusParam = searchParams.get('status');
    if (statusParam) {
      setFilterStatus(statusParam);
    }
  }, [searchParams]);

  const fetchTasks = async () => {
    try {
      const response = await salesApiClient.get('/tasks');
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await salesApiClient.get('/team/members');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const getFilteredTasks = () => {
    return tasks.filter(task => {
      const matchesSearch = searchTerm === '' || 
        task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesUser = filterUser === '' || task.assigned_to === filterUser;
      
      // Status filtering
      const matchesStatus = filterStatus === '' || task.status === filterStatus;
      
      // Date filtering
      let matchesDate = true;
      if (filterDateFrom || filterDateTo) {
        const taskDate = new Date(task.due_date);
        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom);
          fromDate.setHours(0, 0, 0, 0);
          matchesDate = matchesDate && taskDate >= fromDate;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999);
          matchesDate = matchesDate && taskDate <= toDate;
        }
      }
      
      return matchesSearch && matchesUser && matchesDate && matchesStatus;
    });
  };

  const getUserName = (userId) => {
    const u = users.find(x => x.id === userId);
    return u?.name || 'Unassigned';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await salesApiClient.post('/tasks', formData);
      toast.success('Task created successfully');
      setIsDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        due_date: '',
        assigned_to: user.id,
        priority: 'medium',
      });
      fetchTasks();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await salesApiClient.patch(`/tasks/${taskId}`, { status: newStatus });
      toast.success('Task status updated');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-50';
      case 'medium':
        return 'text-amber-600 bg-amber-50';
      case 'low':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-slate-400 bg-slate-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 size={20} className="text-green-500" />;
      case 'in_progress':
        return <Clock size={20} className="text-blue-500" />;
      default:
        return <Circle size={20} className="text-slate-400" />;
    }
  };

  const pendingTasks = tasks.filter((t) => t.status !== 'completed');
  const completedTasks = tasks.filter((t) => t.status === 'completed');

  return (
    <div className="p-6 md:p-8 lg:p-12 space-y-8 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white" data-testid="tasks-title">
            Tasks
          </h1>
          <p className="text-base text-slate-400 mt-2">Manage your daily activities</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-task-button">
              <Plus size={20} className="mr-2" />
              Add Task
            </Button>
          </DialogTrigger>
          <DialogOverlay className="bg-black/80" />
          <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="add-task-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Task</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-200" htmlFor="title">Task Title</Label>
                <Input className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Follow up with CTO"
                  required
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200" htmlFor="description">Description</Label>
                <Textarea className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Discuss contract details..."
                  required
                  data-testid="task-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200" htmlFor="due_date">Due Date</Label>
                <Input className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  id="due_date"
                  type="datetime-local"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                  data-testid="task-due-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200" htmlFor="priority">Priority</Label>
                <select
                  id="priority"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 text-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="task-priority-select"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-task-button">
                Create Task
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input placeholder="Search tasks..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" />
        </div>
        <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)}
          className="h-10 rounded-md border border-slate-700 bg-slate-800 text-white px-3 text-sm">
          <option value="">All Sales Reps</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="h-10 rounded-md border border-slate-700 bg-slate-800 text-white px-3 text-sm">
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">From:</span>
          <Input 
            type="date" 
            value={filterDateFrom} 
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="w-40 h-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">To:</span>
          <Input 
            type="date" 
            value={filterDateTo} 
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="w-40 h-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        {(filterUser || filterDateFrom || filterDateTo || filterStatus) && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setFilterUser('');
              setFilterDateFrom('');
              setFilterDateTo('');
              setFilterStatus('');
            }}
            className="text-slate-400 hover:text-white"
          >
            Clear Filters
          </Button>
        )}
        <div className="flex bg-slate-800 rounded-lg p-1">
          <Button size="sm" variant={viewMode === 'grid' ? 'default' : 'ghost'} onClick={() => setViewMode('grid')}><LayoutGrid size={18} /></Button>
          <Button size="sm" variant={viewMode === 'list' ? 'default' : 'ghost'} onClick={() => setViewMode('list')}><List size={18} /></Button>
        </div>
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && (
      <>
      <div>
        <h2 className="text-xl font-semibold text-white mb-4">Pending Tasks ({getFilteredTasks().filter(t => t.status !== 'completed').length})</h2>
        <div className="space-y-3">
          {getFilteredTasks().filter(t => t.status !== 'completed').map((task) => (
            <Card key={task.id} className="bg-slate-900 border border-slate-800 rounded-xl p-6 hover:border-slate-700">
              <div className="flex items-start gap-4">
                <button onClick={() => handleStatusChange(task.id, task.status === 'completed' ? 'pending' : 'completed')}>
                  {getStatusIcon(task.status)}
                </button>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-white">{task.title}</h3>
                    <span className={`text-xs px-2 py-1 rounded font-medium ${getPriorityColor(task.priority)}`}>{task.priority}</span>
                  </div>
                  <p className="text-sm text-slate-400 mb-2">{task.description}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Clock size={14} /> Due: {format(new Date(task.due_date), 'MMM dd, yyyy h:mm a')}</span>
                    <span className="text-cyan-400">{getUserName(task.assigned_to)}</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
      {getFilteredTasks().filter(t => t.status === 'completed').length > 0 && (
        <div className="mt-6">
          <h2 className="text-xl font-semibold text-white mb-4">Completed ({getFilteredTasks().filter(t => t.status === 'completed').length})</h2>
          <div className="space-y-3">
            {getFilteredTasks().filter(t => t.status === 'completed').map((task) => (
              <Card key={task.id} className="bg-slate-800 border-slate-800 rounded-xl p-6 opacity-60">
                <div className="flex items-start gap-4">
                  <button onClick={() => handleStatusChange(task.id, 'pending')}>{getStatusIcon(task.status)}</button>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white line-through">{task.title}</h3>
                    <p className="text-sm text-slate-400 mt-1">{task.description}</p>
                    <span className="text-xs text-cyan-400">{getUserName(task.assigned_to)}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
      </>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card className="bg-slate-900 border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Task</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Due Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Assigned To</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {getFilteredTasks().map(task => (
                <tr key={task.id} className={`hover:bg-slate-800/50 ${task.status === 'completed' ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">{getStatusIcon(task.status)}</td>
                  <td className="px-4 py-3">
                    <p className={`text-white font-medium ${task.status === 'completed' ? 'line-through' : ''}`}>{task.title}</p>
                    <p className="text-slate-500 text-sm truncate max-w-xs">{task.description}</p>
                  </td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded font-medium ${getPriorityColor(task.priority)}`}>{task.priority}</span></td>
                  <td className="px-4 py-3 text-slate-400 text-sm">{format(new Date(task.due_date), 'MMM dd, h:mm a')}</td>
                  <td className="px-4 py-3 text-cyan-400 text-sm">{getUserName(task.assigned_to)}</td>
                  <td className="px-4 py-3">
                    <Button size="sm" variant="outline" onClick={() => handleStatusChange(task.id, task.status === 'completed' ? 'pending' : 'completed')}>
                      {task.status === 'completed' ? 'Reopen' : 'Complete'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {getFilteredTasks().length === 0 && <div className="p-8 text-center text-slate-400">No tasks found</div>}
        </Card>
      )}

      {tasks.length === 0 && viewMode === 'grid' && (
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-12 text-center" data-testid="no-tasks">
          <AlertCircle size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-400">No tasks yet. Add your first task to get started.</p>
        </Card>
      )}
    </div>
  );
}
